import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'
import { TextField, type TextFieldProps } from './text-field.js'

export interface DebouncedTextFieldProps extends Omit<TextFieldProps, 'onChange' | 'value'> {
  value: string
  onDebouncedChange: (value: string) => void
  debounceMs?: number
}

export const DebouncedTextField: FC<DebouncedTextFieldProps> = props => {
  const { value, onDebouncedChange, debounceMs = 500, ...rest } = props
  const [localValue, setLocalValue] = useState(value)
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value
    setLocalValue(newValue)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      onDebouncedChange(newValue)
    }, debounceMs)
  }

  const handleBlur = () => {
    clearTimeout(timerRef.current)
    if (localValue !== value) {
      onDebouncedChange(localValue)
    }
  }

  useEffect(() => () => clearTimeout(timerRef.current), [])

  return <TextField {...rest} value={localValue} onChange={handleChange} onBlur={handleBlur} />
}
